import { Component, DestroyRef, Input, NgZone, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { SmsTextComponent } from '../sms-text/sms-text.component';
import { formatClock, formatWhen } from '../../../../utils/sms';

/**
 * A message as the Messages app on an iPhone (dark mode) shows it: the sender
 * ID as the contact, the text in an incoming bubble, and the viewer's own
 * time in the status bar.
 */
@Component({
  selector: 'bk-iphone-preview',
  standalone: true,
  imports: [SmsTextComponent],
  templateUrl: './iphone-preview.component.html',
  styleUrl: './iphone-preview.component.css',
})
export class IphonePreviewComponent {
  /** Shown as the contact the message comes from. */
  @Input() senderId = '';
  @Input() text = '';
  /** The text is final, as sent: a {{field}} left in it shows as written, not as a chip. */
  @Input() exact = false;
  /** The time above the message, e.g. when it is scheduled for; null means now. */
  @Input() at: Date | null = null;
  /** Shown on the screen while there is no text. */
  @Input() emptyText = 'Start writing and the message appears here the way an iPhone shows it.';

  /** The status bar's clock: the viewer's time now, kept current while the page is open. */
  clock = formatClock();

  constructor() {
    if (!isPlatformBrowser(inject(PLATFORM_ID))) return;
    const zone = inject(NgZone);
    // Checked each second outside Angular's zone, so the minute turns when the viewer's own
    // clock does; the page only re-renders when it changes.
    const timer = zone.runOutsideAngular(() =>
      setInterval(() => {
        const next = formatClock();
        if (next !== this.clock) zone.run(() => (this.clock = next));
      }, 1_000)
    );
    inject(DestroyRef).onDestroy(() => clearInterval(timer));
  }

  /** The sender's initial on the contact avatar, as Messages draws it. */
  get initial(): string {
    const first = (this.senderId || '').trim().match(/[\p{L}\p{N}]/u);
    return first ? first[0].toUpperCase() : '?';
  }

  /** "Today, 15:42" above the message. */
  get stamp(): string {
    return formatWhen(this.at ?? new Date());
  }
}
